/**
 * /night-vision — the page: camera or file in, the night picture out, and
 * everything a person does with it (looks, capture, watch, the infrared test).
 *
 *   core.js     the algorithm, the controller (gain, noise model, darkness) and «Авто» stacking
 *   gl.js       the GPU executor; core.js CpuPipeline is the fallback lane
 *   camera.js   CameraManager door + zoom/torch/exposure through __trackConstraint
 *   watch.js    motion events and the TV-remote infrared probe
 *   capture.js  photo encoding
 * Video is recorded by the shared WebcamRecorder (WebCodecs → MP4 with AAC,
 * MediaRecorder where WebCodecs is missing) fed with the canvas's own stream;
 * a video FILE is processed frame by frame through WebCodecsPipeline
 * (geometry2d hands over every decoded frame in order).
 *
 * Two layouts of one stage: the page (picture, controls under it, settings
 * beside or below) and camera mode — the stage over the whole screen through
 * __wholeScreen, controls over the picture, settings in a sheet. A phone goes
 * to camera mode when the camera starts (a setting turns that off).
 */
import * as C from './core.js';
import { NightRenderer, sourceMatrix } from './gl.js';
import { NightCamera, isInfraredLabel } from './camera.js';
import { MotionWatch, IrProbe } from './watch.js';
import { encodePhoto } from './capture.js';
import { Roll } from '../online-camera/store.js';

const CFG = window.__NV_CFG || {};
const SLUG = CFG.slug || 'night-vision';
const L = CFG.lang || {};
function T(k, vars) {
    let s = L[k];
    if (s == null) return k;
    if (vars) for (const v in vars) s = s.split(':' + v).join(String(vars[v]));
    return s;
}
/** Plural forms: a translation may be {one, few, many, other} (Intl.PluralRules categories). */
function TP(k, n, vars) {
    const v = L[k];
    if (!v || typeof v !== 'object') return T(k, Object.assign({ n }, vars || {}));
    let cat = 'other';
    try { cat = new Intl.PluralRules(window.__intlLocale ? window.__intlLocale() : undefined).select(n); } catch (_) { }
    let s = v[cat] || v.other || v.many || '';
    const all = Object.assign({ n }, vars || {});
    for (const x in all) s = s.split(':' + x).join(String(all[x]));
    return s;
}
const $ = (id) => document.getElementById(id);
const el = {
    app: $('nvApp'), wrap: $('nvVfWrap'), vf: $('nvVf'), video: $('nvVideo'), canvas: $('nvCanvas'),
    hud: $('nvHud'), vfTools: $('nvVfTools'), split: $('nvSplit'), splitH: $('nvSplitH'),
    hold: $('nvHold'), ring: $('nvRing'), holdText: $('nvHoldText'), flash: $('nvFlash'),
    tip: $('nvTip'), tipText: $('nvTipText'), tipAct: $('nvTipAct'), tipClose: $('nvTipClose'),
    ir: $('nvIr'), irText: $('nvIrText'), irDot: $('nvIrDot'),
    idle: $('nvIdle'), fail: $('nvFail'), failText: $('nvFailText'),
    start: $('nvStart'), retry: $('nvRetry'), openFile: $('nvOpenFile'), failFile: $('nvFailFile'),
    controls: $('nvControls'), looks: $('nvLooks'), camCtl: $('nvCamCtl'), modes: $('nvModes'), shutter: $('nvShutter'),
    flip: $('nvFlip'), torch: $('nvTorch'), compare: $('nvCompare'), last: $('nvLast'), fs: $('nvFs'), settingsOpen: $('nvSettingsOpen'),
    fileCtl: $('nvFileCtl'), playRow: $('nvPlayRow'), play: $('nvPlay'), seek: $('nvSeek'), time: $('nvTime'),
    saveFile: $('nvSaveFile'), saveFrame: $('nvSaveFrame'), closeFile: $('nvCloseFile'),
    exportProg: $('nvExportProg'), exportRow: $('nvExportRow'), exportText: $('nvExportText'), exportCancel: $('nvExportCancel'),
    info: $('nvInfo'), keys: $('nvKeys'), drop: $('nvDrop'),
    rail: $('nvRail'), sheet: $('nvSheet'), sheetBody: $('nvSheetBody'), sheetClose: $('nvSheetClose'), dark: $('nvDark'),
};

/* ───────────────────────── settings ───────────────────────── */

const DEFAULTS = {
    look: 'color', mode: 'photo', stack: 'auto', denoise: 'normal', ev: 0, manGain: false, gainLog: 2,
    shadows: 30, detail: 20, quietColor: true, res: '720', mirror: true, sensor: 'boost', expPos: 60, isoPos: 70,
    tube: 'none', grain: 0, reticle: 'none', stamp: false, redUi: false, camMode: true,
    watchOn: false, watchSens: 50, watchTint: true, watchSound: true, watchTrap: false,
    format: 'jpg', nightLen: 4, sound: false, autoSave: false, facing: 'environment', deviceId: '', v: 2,
};
const S = Object.assign({}, DEFAULTS, load('s', {}));
// settings saved before «Авто» existed: the new default, once
if (!(S.v >= 2)) { S.stack = 'auto'; S.v = 2; }
function load(k, fb) { try { return (window.__toolSettings && window.__toolSettings.load(SLUG, k, fb)) || fb; } catch (_) { return fb; } }
let _saveT = 0;
function persist() {
    clearTimeout(_saveT);
    _saveT = setTimeout(() => { try { window.__toolSettings && window.__toolSettings.save(SLUG, 's', S); } catch (_) { } }, 300);
}

/* ───────────────────────── telemetry ───────────────────────── */

function feat(f, extra) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, f }, extra || {})); } catch (_) { } }
function stage(n, extra) { try { window.__stage && window.__stage(n, extra || {}); } catch (_) { } }
function complete(kind, extra) { try { window.__aeToolComplete && window.__aeToolComplete(Object.assign({ kind }, extra || {})); } catch (_) { } }
function toast(msg, type) { try { window.__toast && window.__toast(msg, type || 'info'); } catch (_) { } }
function announce(msg) { try { window.__announce && window.__announce(msg); } catch (_) { } }
/** A caught error: the label and the exact text go to telemetry, the person reads `say`. */
function oops(err, where, say) {
    try { if (window.__handleError) window.__handleError(err, where, say ? { say } : undefined); } catch (_) { }
}
/*
 * One summary of the visit: what the camera did and what the person did with
 * it — sent when the page is hidden (the last moment a phone reliably sends
 * anything), at most three times a visit, the counters cumulative.
 */
const sess = { camSec: 0, frames: 0, shots: 0, night: 0, recs: 0, recSec: 0, looks: 0, sheet: 0, camMode: 0, autoChanges: 0, gainMax: 0, dark: 0, resumes: 0, files: 0, compare: 0, sent: 0 };
let camOnAt = 0;
function sendSession() {
    if (sess.sent >= 3 || !(sess.frames || sess.files)) return;
    sess.sent++;
    const camSec = sess.camSec + (camOnAt ? (performance.now() - camOnAt) / 1000 : 0);
    feat('session', {
        seg: sess.sent, cam_s: Math.round(camSec), frames: sess.frames, fps: Math.round(procFps), lane, float: R && R.floatRT ? 1 : 0,
        shots: sess.shots, night: sess.night, recs: sess.recs, rec_s: Math.round(sess.recSec), files: sess.files,
        looks: sess.looks, look: S.look, stack: S.stack, auto_lv: S.stack === 'auto' ? auto.level : '', auto_n: sess.autoChanges,
        cam_mode: sess.camMode, sheet: sess.sheet, compare: sess.compare, gain_max: Math.round(sess.gainMax), dark: sess.dark,
        resumes: sess.resumes, gov: gov.scale, watch: watch.on ? 1 : 0, res: S.res, phone: phoneLike() ? 1 : 0,
    });
}

/* ───────────────────────── the engine ───────────────────────── */

let R = null;            // NightRenderer, or null in the CPU lane
let cpu = null;          // { P: CpuPipeline, ctx, tmp, tmpCtx, lastOut } in the CPU lane
let lane = '';           // 'gpu' | 'cpu'
let gpuBroken = false;   // the GPU lane died for good this visit (a lost context that did not come back)
const ctl = new C.Controller();
const auto = new C.AutoStack();
let last = null;         // controller's last answer

function ensureEngine() {
    if (lane) return lane;
    // ?nvlane=cpu — the fallback lane on a machine that has WebGL2 (diagnostics, the stand)
    const force = (location.search.match(/[?&]nvlane=(cpu|gpu|uint)\b/) || [])[1] || '';
    if (force !== 'cpu' && !gpuBroken && NightRenderer.supported()) {
        try {
            R = new NightRenderer(el.canvas, Object.assign(force === 'uint' ? { storage: 'uint' } : {}, { onContext: onGlContext }));
            lane = 'gpu';
            feat('lane', { lane: 'gpu', float: R.floatRT ? 1 : 0 });
            return lane;
        } catch (e) {
            R = null;
            feat('lane_gpu_failed', { err: String((e && e.message) || e).slice(0, 120) });
            freshCanvas();
        }
    }
    lane = 'cpu';
    cpu = { P: null, ctx: el.canvas.getContext('2d'), tmp: document.createElement('canvas'), lastOut: null };
    cpu.tmpCtx = cpu.tmp.getContext('2d', { willReadFrequently: true });
    feat('lane', { lane: 'cpu' });
    return lane;
}
/** The canvas keeps the WebGL context it was given: the 2D lane needs a fresh one. */
function freshCanvas() {
    const fresh = el.canvas.cloneNode(false);
    el.canvas.replaceWith(fresh);
    el.canvas = fresh;
}
/*
 * A lost GPU context (a driver reset, a phone short of memory) usually comes
 * back within a second and gl.js rebuilds itself; the overlay texture is ours
 * to upload again. One that does not come back in 3 s: the CPU lane takes over.
 */
let _lostT = 0;
function onGlContext(what) {
    feat('gl_context', { what });
    clearTimeout(_lostT);
    if (what === 'restored') { drawOverlay(true); return; }
    _lostT = setTimeout(() => {
        if (!R || !R.lost) return;
        gpuBroken = true;
        R = null; lane = '';
        freshCanvas();
        ensureEngine();
        resizeEngine();
        feat('lane_fallback', { why: 'context_lost' });
    }, 3000);
}

/** Processing size for a source of w×h: the quality's long side, the governor's scale, even numbers. */
function procSize(w, h) {
    const cap = lane === 'cpu' ? 480 : ({ 480: 854, 720: 1280, 1080: 1920 }[S.res] || 1280);
    const k = Math.min(1, cap / Math.max(w, h)) * (lane === 'cpu' ? 1 : gov.scale);
    const even = (x) => Math.max(16, Math.round(x / 2) * 2);
    return { w: even(w * k), h: even(h * k) };
}

/** The stacking level in force: the person's, or the one «Авто» picked. */
function stackKey() { return S.stack === 'auto' ? auto.level : S.stack; }

function engineParams() {
    const st = C.STACK[stackKey()] || 12;
    const dr = last && last.logAvg > 0 ? last.p99 / last.logAvg : 0;
    // the local lift is for scenes with a lamp in them; on an evenly dim scene it
    // only moves the picture away from what the camera saw (LOL pairs, docs/why)
    const local = (S.shadows / 100) * 0.6 * C.smoothstep(4, 40, dr);
    return {
        stack: watch.on && st <= 1 ? C.STACK.move : (holdState ? C.STACK.tripod : st),
        denoise: holdState ? C.DENOISE.high : (C.DENOISE[S.denoise] || 0),
        gain: last ? last.gain : 1,
        noise: (last && last.noise) || { a: 5e-7, b: 2e-4 },
        local, detail: (S.detail / 100) * 0.8, desat: S.quietColor ? 0.15 : 0,
        look: S.look, split: splitPos, tube: { none: 0, mono: 1, bino: 2 }[S.tube] || 0,
        grain: (S.grain / 100) * 0.25, motionTint: watch.on && S.watchTint ? 0.55 : 0,
        dither: 1, align: true,
    };
}

/* Governor: when the GPU cannot keep up with the camera, process fewer pixels. */
const gov = { scale: 1, frames: 0, t0: 0, slow: 0 };
function governor(now, srcFps) {
    if (!gov.t0) { gov.t0 = now; gov.frames = 0; return; }
    gov.frames++;
    const dt = (now - gov.t0) / 1000;
    if (dt < 3) return;
    const fps = gov.frames / dt;
    gov.t0 = now; gov.frames = 0;
    procFps = fps;
    if (srcFps > 0 && fps < Math.min(20, srcFps * 0.7) && gov.scale > 0.5) {
        gov.slow++;
        if (gov.slow >= 2) {
            gov.scale = gov.scale > 0.8 ? 0.75 : 0.5;
            gov.slow = 0;
            feat('governor', { scale: gov.scale, fps: Math.round(fps) });
            resizeEngine();
        }
    } else gov.slow = 0;
}
let procFps = 0;

let srcW = 0, srcH = 0, procW = 0, procH = 0;
function resizeEngine() {
    if (!srcW) return;
    const s = procSize(srcW, srcH);
    procW = s.w; procH = s.h;
    el.vf.style.setProperty('--nv-ar', (srcW / srcH).toFixed(4));
    if (lane === 'gpu') R.resize(procW, procH);
    else {
        el.canvas.width = procW; el.canvas.height = procH;
        cpu.tmp.width = procW; cpu.tmp.height = procH;
        cpu.P = new C.CpuPipeline(procW, procH);
        cpu.lastOut = null;
    }
    drawOverlay(true);
}

function mirrored() { return source === 'camera' && S.mirror && cam && cam.facing === 'user'; }
function matrix() { return sourceMatrix({ zoom: digitalZoom, mirror: mirrored(), rotate: 0 }); }

/**
 * One frame through the engine: upload, render, read the controller.
 * Returns false when there was no frame (an interrupted camera).
 */
function processFrame(src, dt) {
    if (lane === 'gpu') {
        if (!R.upload(src)) return false;
        R.set(engineParams());
        if (!R.render(matrix())) return false;
        if (R.stats && R.stats.frame !== lastStatsFrame) {
            lastStatsFrame = R.stats.frame;
            onStats(R.stats, dt);
        }
    } else {
        const x = cpu.tmpCtx;
        // the same zoom and mirror as the GPU lane's source matrix
        const z = source === 'camera' ? digitalZoom : 1, mir = mirrored();
        x.save();
        if (mir) { x.translate(procW, 0); x.scale(-1, 1); }
        if (z > 1) { x.translate(procW / 2, procH / 2); x.scale(z, z); x.translate(-procW / 2, -procH / 2); }
        let drew = true;
        if (src && src.tagName === 'VIDEO') drew = !!window.__drawCameraFrame(x, src, 0, 0, procW, procH);
        else x.drawImage(src, 0, 0, procW, procH);
        x.restore();
        if (!drew) return false;
        const id = x.getImageData(0, 0, procW, procH);
        const p = engineParams();
        const out = cpu.P.process(id.data, Object.assign({}, p, { lut: p.look === 'color' ? null : C.paletteLUT(p.look), zTest: true }));
        cpu.lastOut = new ImageData(out, procW, procH);
        cpu.ctx.putImageData(cpu.lastOut, 0, 0);
        if (overlayCanvas && overlayOn) cpu.ctx.drawImage(overlayCanvas, 0, 0);
        // compare: the camera's own frame left of the line, as the GPU lane draws it
        if (splitPos > 0) { const sx = Math.round(splitPos * procW); if (sx > 0) cpu.ctx.drawImage(cpu.tmp, 0, 0, sx, procH, 0, 0, sx, procH); }
        onStats(cpu.P.stats(id.data, 48, 27, frameNo), dt);
    }
    frameNo++;
    return true;
}
let lastStatsFrame = -1, frameNo = 0;

/**
 * The picture as a 2D canvas for a photo — never with the compare line: the
 * GPU lane draws the output pass again without it, the CPU lane keeps the
 * processed frame. Same task as the render it follows.
 */
function cleanSnapshot() {
    if (lane === 'gpu') {
        if (splitPos > 0) R.redraw({ split: 0 });
        return R.snapshot();
    }
    const c = document.createElement('canvas');
    c.width = procW; c.height = procH;
    const x = c.getContext('2d');
    if (cpu.lastOut) x.putImageData(cpu.lastOut, 0, 0); else x.drawImage(el.canvas, 0, 0);
    if (overlayCanvas && overlayOn) x.drawImage(overlayCanvas, 0, 0);
    return c;
}

function onStats(st, dt) {
    last = ctl.update(st, Math.max(0.001, dt || 0.033), { ev: S.ev, manualGain: S.manGain ? Math.pow(2, S.gainLog) : 0 });
    if (last.gain > sess.gainMax) sess.gainMax = last.gain;
    if (S.stack === 'auto' && (source === 'camera' || source === 'video')) autoStep(dt);
    if (source === 'camera') sensorFollowsLight();
    if (watch.on && source === 'camera') watchStep(st);
    if (ir.on) irStep(st);
}

/* «Авто» stacking: the level follows the camera's shake (core.js AutoStack). */
function shakeRef() { return last && last.shakePx != null && procW ? last.shakePx * 1280 / procW : null; }
function autoStep(dt) {
    const before = auto.level;
    auto.update(shakeRef(), last.motionFrac, Math.max(0.001, dt || 0.033));
    if (auto.level === before) return;
    sess.autoChanges++;
    if (sess.autoChanges <= 20) { const sh = shakeRef(); feat('auto_stack', { from: before, to: auto.level, shake: sh == null ? -1 : Math.round(sh * 10) / 10, mf: Math.round(last.motionFrac * 100) }); }
    paintStackHint();
}
function paintStackHint() {
    $('nvStackHint').textContent = S.stack === 'auto'
        ? T('stack_auto_now', { lv: T('stack_' + auto.level) }) + ' ' + T('stack_hint_auto')
        : T('stack_hint_' + S.stack);
}

/* ───────────────────────── sources ───────────────────────── */

let source = '';         // '' | 'camera' | 'image' | 'video'
let cam = null;
let loopOn = false, lastT = 0, rvfcId = 0, rafId = 0, lastMediaTime = -1, lastPresented = -1;
let srcFpsEst = 30;
let openT0 = 0, firstFrameSeen = true;

function startLoop() {
    if (loopOn) return;
    loopOn = true; lastT = performance.now(); lastMediaTime = -1;
    gov.t0 = 0;
    tick();
}
function stopLoop() {
    loopOn = false;
    try { if (rvfcId && el.video.cancelVideoFrameCallback) el.video.cancelVideoFrameCallback(rvfcId); } catch (_) { }
    if (rafId) cancelAnimationFrame(rafId);
    rvfcId = 0; rafId = 0;
}
/*
 * New frames only: the temporal average must see each camera frame once (a
 * frame seen twice would count as two looks at the scene and fake a quieter
 * picture). requestVideoFrameCallback says exactly when a new frame is shown;
 * without it, the frame counter of the element decides.
 */
function tick() {
    if (!loopOn) return;
    const v = el.video;
    if (v.requestVideoFrameCallback) {
        rvfcId = v.requestVideoFrameCallback((now, meta) => {
            rvfcId = 0;
            if (meta && meta.presentedFrames != null) {
                if (lastPresented >= 0 && meta.presentedFrames > lastPresented) {
                    const df = meta.presentedFrames - lastPresented;
                    const dtf = (now - lastT) / 1000;
                    if (dtf > 0) srcFpsEst = 0.9 * srcFpsEst + 0.1 * (df / dtf);
                }
                lastPresented = meta.presentedFrames;
            }
            frame(now);
            tick();
        });
        // a callback that stopped coming (covered element on some engines) falls back to rAF
        clearTimeout(tick._w);
        tick._w = setTimeout(() => { if (loopOn && rvfcId && performance.now() - lastT > 1500) { try { v.cancelVideoFrameCallback(rvfcId); } catch (_) { } rvfcId = 0; rafLoop(); } }, 1600);
    } else rafLoop();
}
function rafLoop() {
    if (!loopOn) return;
    rafId = requestAnimationFrame((now) => {
        rafId = 0;
        const v = el.video;
        const q = v.getVideoPlaybackQuality ? v.getVideoPlaybackQuality().totalVideoFrames : -1;
        const mt = q > 0 ? q : v.currentTime;
        if (mt !== lastMediaTime) { lastMediaTime = mt; frame(now); }
        rafLoop();
    });
}

function frame(now) {
    const dt = Math.min(0.5, (now - lastT) / 1000);
    lastT = now;
    if (exporting) return;
    if (source === 'camera' || source === 'video') {
        const v = el.video;
        if (v.videoWidth && (v.videoWidth !== srcW || v.videoHeight !== srcH)) { srcW = v.videoWidth; srcH = v.videoHeight; resizeEngine(); }
        if (!processFrame(v, dt)) return;
        sess.frames++;
        if (!firstFrameSeen && source === 'camera') { firstFrameSeen = true; stage('first_frame', { ms: Math.round(performance.now() - openT0), lane }); }
        governor(now, source === 'camera' ? srcFpsEst : 0);
        afterRender(now);
    }
}

/** Things that need the picture still on the canvas: shots; and the throttled HUD. */
function afterRender(now) {
    if (pendingShot) { const p = pendingShot; pendingShot = null; takeShot(p); }
    if (holdState) holdStep(now);
    if (now - hudT > 250) { hudT = now; updateHud(); }
    if (source === 'video') updateTime();
    if (overlayOn && S.stamp && now - overlayT > 1000) drawOverlay(false);
}
let hudT = 0;

/* ── camera ── */

let _opening = false;
async function openCamera(btn, how) {
    if (_opening) return;
    _opening = true;
    try { await openCameraNow(btn, how); } finally { _opening = false; }
}
async function openCameraNow(btn, how) {
    if (source && source !== 'camera') closeFile(true);
    ensureEngine();
    if (!window.CameraManager) { showFail(T('cam_module_missing')); return; }
    cam = cam || new NightCamera({ video: el.video, slug: SLUG });
    stopLoop();
    el.idle.hidden = true; el.fail.hidden = true;
    el.info.textContent = T('cam_wait');
    openT0 = performance.now();
    stage('camera_open', { how: how || 'button' });
    try {
        await cam.open({ facingMode: S.facing, deviceId: S.deviceId || undefined, height: +S.res, button: btn || el.start });
    } catch (e) {
        source = '';
        setControls();
        const msg = cam.hasHelp() ? T('cam_fail_alt') : (cam.errorMessage(e) + ' ' + T('cam_fail_alt'));
        showFail(msg);
        feat('camera_fail', { code: (e && e.code) || '', how: how || 'button' });
        return;
    }
    source = 'camera';
    srcW = el.video.videoWidth; srcH = el.video.videoHeight;
    resizeEngine();
    if (R) R.reset(); else if (cpu.P) cpu.P.reset();
    ctl.reset(); last = null;
    auto.reset();
    digitalZoom = 1;
    setControls();
    firstFrameSeen = false;
    startLoop();
    if (!camOnAt) camOnAt = performance.now();
    try { window.__wakeLock && window.__wakeLock.hold('night-vision'); } catch (_) { }
    if (cam.track) cam.track.addEventListener('ended', onTrackEnded, { once: true });
    stage('camera_on', { ms: Math.round(performance.now() - openT0), how: how || 'button' });
    feat('camera_on', { w: srcW, h: srcH, facing: cam.facing || '', lane, float: R && R.floatRT ? 1 : 0, res: S.res, how: how || 'button' });
    // a phone: the camera takes the whole screen, the way its own camera app does
    if (!how && S.camMode && phoneLike() && !wsOn) enterCamMode('auto');
    await afterCameraOpen();
}

async function afterCameraOpen() {
    // what this camera lets a page do — the census both the person and we want to see
    const c = cam.census();
    renderCensus(c);
    feat('census', { zoom: c.zoom ? Math.round(c.zoom.max * 10) / 10 : 0, torch: c.torch ? 1 : 0, et: c.exposureTime ? 1 : 0, iso: c.iso ? 1 : 0, ec: c.exposureCompensation ? 1 : 0, fr: c.frameRate ? Math.round(c.frameRate.max) : 0 });
    el.torch.hidden = !c.torch;
    setupZoom(c);
    setupSensor(c);
    sensorBoosted = null; sensorT = 0;
    if (S.sensor !== 'auto') applySensor();
    fillDevices();
    $('nvMirrorWrap').hidden = cam.facing !== 'user';
    watchBattery();
}

async function closeCamera() {
    stopLoop();
    if (recState) await stopRecording(true);
    cancelHold();
    if (cam) await cam.close();
    if (source === 'camera') source = '';
    if (camOnAt) { sess.camSec += (performance.now() - camOnAt) / 1000; camOnAt = 0; }
    torchOn = false;
    el.torch.setAttribute('aria-pressed', 'false');
    try { window.__wakeLock && window.__wakeLock.releaseHold('night-vision'); } catch (_) { }
}

/** The camera went away while the page was looking at it (unplugged, taken by another app). */
async function onTrackEnded() {
    if (source !== 'camera' || document.hidden) return;   // a hidden page reopens on return (resumeCamera)
    feat('camera_lost');
    await closeCamera();
    setControls();
    showFail(T('cam_lost'));
}

/*
 * Back from another app or tab: iOS stops the camera of a hidden page, Android
 * may hand the camera to another app. A live track gets two seconds to send
 * frames again; otherwise the camera is opened anew — the person comes back to
 * a moving picture, not a frozen one.
 */
let hiddenAt = 0;
async function resumeCamera() {
    if (source !== 'camera' || !cam) return;
    const away = hiddenAt ? Math.round((performance.now() - hiddenAt) / 1000) : 0;
    const t = cam.track;
    if (t && t.readyState === 'live') {
        const f0 = frameNo;
        await new Promise((r) => setTimeout(r, 2000));
        if (source !== 'camera' || document.hidden) return;
        if (frameNo > f0) { feat('resume', { how: 'live', away }); return; }
    }
    sess.resumes++;
    feat('resume', { how: 'reopen', away, ended: !t || t.readyState === 'ended' ? 1 : 0 });
    await openCamera(null, 'resume');
}

function showFail(text) {
    el.vf.classList.add('nv-vf-empty');
    el.idle.hidden = true;
    el.fail.hidden = false;
    el.failText.textContent = text;
    el.info.textContent = '';
}

async function fillDevices() {
    const sel = $('nvDevice');
    const list = await cam.devices();
    const cur = cam.track && cam.track.getSettings ? (cam.track.getSettings().deviceId || '') : '';
    sel.textContent = '';
    const o0 = document.createElement('option'); o0.value = ''; o0.textContent = T('camera_default'); sel.appendChild(o0);
    let ir = false;
    list.forEach((d, i) => {
        const o = document.createElement('option');
        o.value = d.deviceId; o.textContent = d.label || (T('camera_n', { n: i + 1 }));
        if (isInfraredLabel(d.label)) { ir = true; o.textContent += ' · ' + T('ir_badge'); }
        if (d.deviceId === cur) o.selected = true;
        sel.appendChild(o);
    });
    $('nvIrCamNote').hidden = !ir;
    el.flip.disabled = list.length < 2;
    el.flip.classList.toggle('nv-ghost', list.length < 2);
    $('nvCamSum').textContent = cam.label ? cam.label.replace(/\s*\(.*\)$/, '').slice(0, 28) : '';
    if (ir) feat('ir_camera_listed');
}

/* ── battery: a night out with the phone — say when it is running low ── */

let batt = null, _battWired = false;
function watchBattery() {
    const B = window.__batteryStatus;
    if (!B || _battWired) return;
    _battWired = true;
    const take = (r) => {
        batt = r && r.state === 'ok' && !r.charging && r.percent != null ? r.percent : null;
        if (batt != null && batt <= 15 && !watchBattery._told) { watchBattery._told = true; feat('battery_low', { p: batt }); }
    };
    try { B.read().then(take, () => { }); B.onChange(take); } catch (_) { }
}

/* ── zoom: the camera's own where it has one, else the page enlarges ── */

let digitalZoom = 1, nativeZoom = null, zoomVal = 1;
function setupZoom(c) {
    nativeZoom = c.zoom;
    const zr = $('nvZoom');
    if (nativeZoom) {
        zr.min = String(nativeZoom.min); zr.max = String(Math.min(nativeZoom.max, 10)); zr.step = String(nativeZoom.step || 0.1);
        zoomVal = cam.settings.zoom || nativeZoom.min;
        $('nvZoomNote').textContent = T('zoom_native');
    } else {
        zr.min = '1'; zr.max = '4'; zr.step = '0.1';
        zoomVal = digitalZoom;
        $('nvZoomNote').textContent = T('zoom_digital');
    }
    zr.value = String(zoomVal);
    $('nvZoomWrap').hidden = false;
    showZoom();
}
let _zoomT = 0;
function setZoom(z) {
    const zr = $('nvZoom');
    z = Math.min(+zr.max, Math.max(+zr.min, z));
    zoomVal = z; zr.value = String(z);
    showZoom();
    if (nativeZoom && cam) {
        clearTimeout(_zoomT);
        _zoomT = setTimeout(() => { cam.setZoom(zoomVal).then((r) => { if (!r.ok) feat('zoom_refused', { reason: r.reason }); }); }, 40);
    } else {
        if (Math.abs(z - digitalZoom) / digitalZoom > 0.02) { if (R) R.reset(); else if (cpu && cpu.P) cpu.P.reset(); }
        digitalZoom = z;
    }
}
function zoomShown() { return zoomVal / (nativeZoom ? (nativeZoom.min || 1) : 1); }
function showZoom() { $('nvZoomV').textContent = '×' + zoomShown().toFixed(1); }

/* ── torch ── */

let torchOn = false;
async function toggleTorch() {
    if (!cam || !cam.hasTorch()) return;
    const want = !torchOn;
    const r = await cam.setTorch(want);
    if (r.ok) { torchOn = want; }
    else toast(T('torch_fail'), 'error');
    el.torch.setAttribute('aria-pressed', String(torchOn));
    if (tipKey === 'dark') el.tipAct.textContent = '🔦 ' + (torchOn ? T('torch_off') : T('torch_on'));
    feat('torch', { on: torchOn ? 1 : 0, ok: r.ok ? 1 : 0 });
}

/* ── sensor ── */

function setupSensor(c) {
    const any = c.exposureCompensation || c.frameRate || c.exposureTime;
    $('nvSensorWrap').hidden = !any;
    const seg = $('nvSensor');
    seg.querySelector('[data-v="manual"]').disabled = !c.exposureTime;
    if (!c.exposureTime && S.sensor === 'manual') S.sensor = 'boost';
    paintSeg(seg, S.sensor);
    $('nvManual').hidden = S.sensor !== 'manual';
    $('nvIsoWrap').hidden = !c.iso;
    $('nvSensorHint').textContent = any ? T('sensor_hint') : T('sensor_none');
    if (!any) { $('nvSensorWrap').hidden = false; seg.hidden = true; }
    else seg.hidden = false;
    showManualValues();
}
function logPos(range, pos) {
    const lo = Math.max(range.min, 1e-6), hi = Math.max(range.max, lo * 1.0001);
    return lo * Math.pow(hi / lo, pos / 100);
}
function showManualValues() {
    if (!cam) return;
    const et = cam.range('exposureTime'), iso = cam.range('iso');
    if (et) {
        const v = logPos(et, S.expPos), ms = v / 10;
        $('nvExpV').textContent = ms >= 1000 ? (ms / 1000).toFixed(1) + ' ' + T('unit_s') : ms >= 100 ? Math.round(ms) + ' ' + T('unit_ms') : '1/' + Math.round(1000 / ms) + ' ' + T('unit_s');
    }
    if (iso) $('nvIsoV').textContent = String(Math.round(logPos(iso, S.isoPos)));
}
let _sensorT = 0;
function applySensor() {
    clearTimeout(_sensorT);
    _sensorT = setTimeout(async () => {
        if (!cam || !cam.track) return;
        const et = cam.range('exposureTime'), iso = cam.range('iso');
        const r = await cam.setExposure(S.sensor, { time: et ? logPos(et, S.expPos) : 0, iso: iso ? logPos(iso, S.isoPos) : 0 });
        feat('sensor', { mode: S.sensor, ok: r.ok ? 1 : 0, reason: r.reason || '' });
        if (!r.ok && S.sensor !== 'auto') toast(T('sensor_refused'), 'error');
    }, 150);
}

/*
 * «More light» is for the dark: in a lit room a long exposure only clips. While
 * the mode is chosen, the camera's own exposure follows the scene — back to
 * plain auto above a mean code of 110, more light again under 60, at most
 * every 3 s (hysteresis: a lamp switched on and off must not make it pump).
 */
let sensorBoosted = null, sensorT = 0;
function sensorFollowsLight() {
    if (S.sensor !== 'boost' || !cam || !last || $('nvSensorWrap').hidden) return;
    const now = performance.now();
    if (now - sensorT < 3000) return;
    const want = sensorBoosted === false ? last.rawMeanCode < 60 : !(last.rawMeanCode > 110);
    if (want === sensorBoosted) return;
    sensorT = now;
    sensorBoosted = want;
    cam.setExposure(want ? 'boost' : 'auto', {}).then((r) => feat('sensor_follow', { boost: want ? 1 : 0, code: Math.round(last.rawMeanCode), ok: r.ok ? 1 : 0 }));
}

function renderCensus(c) {
    const ul = $('nvCensus');
    ul.textContent = '';
    const row = (ok, text) => { const li = document.createElement('li'); const a = document.createElement('span'); a.textContent = ok ? '✓' : '—'; const b = document.createElement('span'); b.textContent = text; li.append(a, b); ul.appendChild(li); };
    row(!!c.zoom, c.zoom ? T('census_zoom', { max: (c.zoom.max / (c.zoom.min || 1)).toFixed(1) }) : T('census_zoom_none'));
    row(c.torch, c.torch ? T('census_torch') : T('census_torch_none'));
    row(!!c.exposureTime, c.exposureTime ? T('census_exposure', { max: Math.round(c.exposureTime.max / 10) }) : T('census_exposure_none'));
    row(!!c.iso, c.iso ? T('census_iso', { min: Math.round(c.iso.min), max: Math.round(c.iso.max) }) : T('census_iso_none'));
    row(!!c.exposureCompensation, c.exposureCompensation ? T('census_ec') : T('census_ec_none'));
    if (!c.exposureTime && !c.exposureCompensation && /iPhone|iPad|Macintosh/.test(navigator.userAgent) && !/Chrome|CriOS|Android/.test(navigator.userAgent)) {
        const li = document.createElement('li'); li.textContent = T('census_safari'); ul.appendChild(li);
    }
}

/* ───────────────────────── controls ───────────────────────── */

function paintSeg(seg, v) {
    seg.querySelectorAll('button[data-v]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.v === String(v))));
}
function bindSeg(id, key, after) {
    const seg = $(id);
    paintSeg(seg, S[key]);
    seg.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-v]');
        if (!b || b.disabled) return;
        const v = typeof DEFAULTS[key] === 'number' ? +b.dataset.v : b.dataset.v;
        S[key] = v; paintSeg(seg, v); persist();
        if (after) after(v);
        feat('set', { k: key, v: String(v) });
    });
}
function bindRange(id, key, show, after) {
    const r = $(id);
    r.value = String(S[key]);
    const paint = () => { if (show) show(+r.value); };
    paint();
    r.addEventListener('input', () => { S[key] = +r.value; paint(); persist(); if (after) after(+r.value); });
    r.addEventListener('change', () => feat('set', { k: key, v: String(r.value) }));
}
function bindCheck(id, key, after) {
    const c = $(id);
    c.checked = !!S[key];
    c.addEventListener('change', () => { S[key] = c.checked; persist(); if (after) after(c.checked); feat('set', { k: key, v: c.checked ? 1 : 0 }); });
}

const SWATCH = { color: 'linear-gradient(135deg,#1f3b73,#3b8c5a 45%,#d9a441)' };
function swatchCss(name) {
    if (SWATCH[name]) return SWATCH[name];
    const stops = C.PALETTES[name];
    return 'linear-gradient(90deg,' + stops.map((s) => 'rgb(' + Math.round(s[1] * 255) + ',' + Math.round(s[2] * 255) + ',' + Math.round(s[3] * 255) + ') ' + Math.round(s[0] * 100) + '%').join(',') + ')';
}
function setLook(v, how) {
    S.look = v; persist();
    el.looks.querySelectorAll('.nv-look').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.look === v)));
    const thermal = v === 'iron' || v === 'rainbow' || v === 'blackhot';
    $('nvThermalNote').hidden = !thermal;
    $('nvLookSum').textContent = T('look_' + v);
    $('nvLookName').textContent = T('look_' + v);
    redUi();
    if (source === 'image') renderStill();
    drawOverlay(true);
    if (how) { sess.looks++; feat('look', { v, how }); }
    if (thermal && how && !setLook._told) { setLook._told = true; toast(T('thermal_note'), 'info'); }
}
function redUi() {
    const on = S.redUi || (S.look === 'red' && wsOn);
    el.app.classList.toggle('nv-redui', on);
}

function setMode(m) {
    if (recState) return;
    S.mode = m; persist();
    el.modes.querySelectorAll('.nv-mode').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === m)));
    el.shutter.dataset.mode = m;
    const lbl = T('shutter_' + m);
    el.shutter.setAttribute('aria-label', lbl); el.shutter.title = lbl;
}

/** What is on screen follows the source: nothing — the invitation; a picture — its tools. */
function setControls() {
    const has = !!source, live = source === 'camera', isFile = source === 'image' || source === 'video';
    el.controls.hidden = !has;
    el.vfTools.hidden = !has;
    el.hud.hidden = !has;
    el.shutter.disabled = !live;
    el.camCtl.hidden = !live;
    el.fileCtl.hidden = !isFile;
    $('nvLimitWrap').hidden = !isFile;
    el.torch.hidden = !(live && cam && cam.hasTorch());
    el.keys.hidden = !live;
    el.idle.hidden = has;
    if (has) el.fail.hidden = true;
    el.vf.classList.toggle('nv-vf-empty', !has);
    if (!has) {
        if (splitPos > 0) setSplit(false);
        hideTip();
        dimOff();
        if (wsOn && ws) ws.exit();
    }
    if (window.WideWorkspace && window.WideWorkspace.sync) window.WideWorkspace.sync();
}

/* ── compare: the camera's own frame on the left of a line you can drag ── */

let splitPos = 0;
function setSplit(on) {
    splitPos = on ? (splitPos > 0.02 && splitPos < 0.98 ? splitPos : 0.5) : 0;
    el.split.hidden = !on;
    placeSplit();
    el.compare.setAttribute('aria-pressed', String(!!on));
    if (source === 'image') renderStill();
    if (on) { sess.compare++; feat('compare', { src: source }); }
}
function placeSplit() { el.split.style.left = (splitPos * 100).toFixed(2) + '%'; }
(function wireSplit() {
    let drag = false;
    const move = (e) => {
        const r = el.vf.getBoundingClientRect();
        splitPos = Math.min(0.98, Math.max(0.02, (e.clientX - r.left) / r.width));
        placeSplit();
        if (source === 'image') renderStill();
    };
    el.splitH.addEventListener('pointerdown', (e) => { drag = true; el.splitH.setPointerCapture(e.pointerId); e.preventDefault(); });
    el.splitH.addEventListener('pointermove', (e) => { if (drag) move(e); });
    el.splitH.addEventListener('pointerup', () => { drag = false; });
    el.splitH.addEventListener('pointercancel', () => { drag = false; });
    el.splitH.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            splitPos = Math.min(0.98, Math.max(0.02, splitPos + (e.key === 'ArrowRight' ? 0.05 : -0.05)));
            placeSplit(); e.preventDefault();
            if (source === 'image') renderStill();
        }
    });
})();

/* ── pinch to zoom on the picture ── */

(function wirePinch() {
    const pts = new Map();
    let d0 = 0, z0 = 1;
    el.vf.addEventListener('pointerdown', (e) => {
        if (e.target === el.splitH) return;
        pts.set(e.pointerId, e);
        if (pts.size === 2) { const [a, b] = [...pts.values()]; d0 = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY); z0 = zoomVal; }
    });
    el.vf.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, e);
        if (pts.size === 2 && d0 > 20 && source === 'camera') {
            const [a, b] = [...pts.values()];
            const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            setZoom(z0 * d / d0);
        }
    });
    const up = (e) => { pts.delete(e.pointerId); if (pts.size < 2) d0 = 0; };
    el.vf.addEventListener('pointerup', up); el.vf.addEventListener('pointercancel', up); el.vf.addEventListener('pointerleave', up);
})();

/* ── the tip over the picture: one at a time, a closed one stays closed until its cause goes ── */

let tipKey = '';
const tipClosed = new Set();
function showTip(key, text, act) {
    if (tipClosed.has(key)) return;
    tipKey = key;
    el.tipText.textContent = text;
    el.tipAct.hidden = !act;
    el.tipAct.textContent = act ? act.label : '';
    el.tipAct.onclick = act ? act.run : null;
    el.tip.hidden = false;
}
function hideTip(key) {
    if (key && tipKey !== key) return;
    el.tip.hidden = true;
    tipKey = '';
}

/* ───────────────────────── HUD ───────────────────────── */

let darkSince = 0, lightSince = 0, darkShown = false;
function updateHud() {
    const pills = [];
    if (last) {
        pills.push({ t: (S.manGain ? T('hud_manual') + ' ' : '') + '×' + fmtGain(last.gain) });
        const st = engineParams().stack;
        if (st > 1) pills.push({ t: TP('hud_frames', Math.max(1, Math.round(last.history || 1))) });
    }
    if (source === 'camera' && zoomShown() > 1.05) pills.push({ t: T('hud_zoom', { z: zoomShown().toFixed(1) }) });
    if (recState) pills.push({ t: 'REC ' + fmtTime(recSec()) + (recBytes ? ' · ' + (recBytes / 1048576).toFixed(0) + ' ' + T('unit_mb') : ''), cls: 'rec' });
    if (alarmUntil > performance.now()) pills.push({ t: '⚠ ' + T('hud_motion'), cls: 'alarm' });
    if (source === 'camera' && batt != null && batt <= 15) pills.push({ t: T('hud_battery', { p: batt }), cls: 'warn' });
    renderPills(pills);
    // darkness: «the camera gets essentially no light» held for 2 s
    const now = performance.now();
    if (last && last.dark && source === 'camera') { lightSince = 0; if (!darkSince) darkSince = now; }
    else { darkSince = 0; if (!lightSince) lightSince = now; }
    const want = darkSince && now - darkSince > 2000;
    if (want && !darkShown) {
        darkShown = true; sess.dark++;
        const torch = cam && cam.hasTorch();
        showTip('dark', T('dark_tip'), torch ? { label: '🔦 ' + (torchOn ? T('torch_off') : T('torch_on')), run: toggleTorch } : null);
        feat('dark_tip', { code: Math.round(last.rawMeanCode * 10) / 10, snr: Math.round(last.snr * 10) / 10, torch: torch ? 1 : 0 });
    }
    if (!want && darkShown && lightSince && now - lightSince > 2000) { darkShown = false; hideTip('dark'); tipClosed.delete('dark'); }
    // the info line
    const bits = [];
    if (procW) bits.push(procW + '×' + procH);
    if (procFps) bits.push(Math.round(procFps) + ' ' + T('unit_fps'));
    bits.push(lane === 'gpu' ? T('lane_gpu') : T('lane_cpu'));
    if (gov.scale < 1) bits.push(T('lane_reduced'));
    el.info.textContent = bits.join(' · ');
    $('nvStackNow').textContent = last && engineParams().stack > 1 ? TP('stack_now', Math.max(1, Math.round(last.history || 1))) : '';
}
function renderPills(list) {
    const key = list.map((p) => p.t + '|' + (p.cls || '')).join('/');
    if (key === renderPills._k) return;
    renderPills._k = key;
    el.hud.textContent = '';
    for (const p of list) { const s = document.createElement('span'); s.className = 'nv-pill' + (p.cls ? ' ' + p.cls : ''); s.textContent = p.t; el.hud.appendChild(s); }
}
function fmtGain(g) { return g >= 10 ? String(Math.round(g)) : g.toFixed(1); }
function fmtTime(s) { s = Math.max(0, Math.round(s)); const m = Math.floor(s / 60); return m + ':' + String(s % 60).padStart(2, '0'); }

/* ── overlay: reticle and the time stamp, burned into photos and video ── */

let overlayCanvas = null, overlayOn = false, overlayT = 0;
function drawOverlay(force) {
    const want = S.reticle !== 'none' || S.stamp;
    if (!want || !procW) {
        if (overlayOn) { overlayOn = false; if (R) R.setOverlay(null); }
        return;
    }
    if (!overlayCanvas) overlayCanvas = document.createElement('canvas');
    const c = overlayCanvas;
    if (c.width !== procW || c.height !== procH) { c.width = procW; c.height = procH; }
    const x = c.getContext('2d');
    x.clearRect(0, 0, procW, procH);
    const ink = { green: '#9dff8a', white: '#e8eefc', red: '#ff6b5e', mono: '#ffffff' }[S.look] || '#ffe08a';
    const u = Math.min(procW, procH);
    x.strokeStyle = ink; x.fillStyle = ink; x.lineWidth = Math.max(1, u / 360);
    x.globalAlpha = 0.85;
    const cx = procW / 2, cy = procH / 2;
    if (S.reticle === 'cross') {
        const g = u * 0.03, l = u * 0.12;
        x.beginPath();
        x.moveTo(cx - l, cy); x.lineTo(cx - g, cy); x.moveTo(cx + g, cy); x.lineTo(cx + l, cy);
        x.moveTo(cx, cy - l); x.lineTo(cx, cy - g); x.moveTo(cx, cy + g); x.lineTo(cx, cy + l);
        x.stroke();
        for (let i = 1; i <= 4; i++) { const t = g + (l - g) * i / 4; x.fillRect(cx - x.lineWidth * 2, cy + t, x.lineWidth * 4, x.lineWidth); }
    } else if (S.reticle === 'circle') {
        x.beginPath(); x.arc(cx, cy, u * 0.09, 0, Math.PI * 2); x.stroke();
        x.beginPath(); x.arc(cx, cy, Math.max(1.5, u / 260), 0, Math.PI * 2); x.fill();
    }
    if (S.stamp) {
        const fs = Math.max(12, Math.round(u * 0.035));
        x.font = '700 ' + fs + 'px ui-monospace, "Courier New", monospace';
        x.textBaseline = 'bottom';
        x.globalAlpha = 0.9;
        x.shadowColor = 'rgba(0,0,0,.8)'; x.shadowBlur = fs * 0.3;
        const d = new Date();
        const ts = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + ' ' + d.toTimeString().slice(0, 8);
        const extra = last ? '  ×' + fmtGain(last.gain) + (zoomVal > 1.05 ? '  ZOOM ' + zoomVal.toFixed(1) : '') : '';
        x.fillText(ts + extra, fs * 0.8, procH - fs * 0.6);
    }
    overlayOn = true; overlayT = performance.now();
    if (R) R.setOverlay(c);
    void force;
}

/* ───────────────────────── capture ───────────────────────── */

const roll = new Roll('timbrica-night-vision');
let rollItems = [];
let pendingShot = null;
let holdState = null;

function shutter() {
    if (source !== 'camera' || !cam || !cam.live) return;
    if (S.mode === 'video') { if (recState) stopRecording(); else startRecording(); return; }
    if (S.mode === 'night') { startHold(); return; }
    pendingShot = { kind: 'photo', gesture: true };
}

function startHold() {
    if (holdState) return;
    const sec = S.nightLen || 4;
    holdState = { t0: performance.now(), sec, frames: 0 };
    if (R) R.reset(); else if (cpu && cpu.P) cpu.P.reset();
    el.hold.hidden = false;
    el.shutter.dataset.state = 'busy'; el.shutter.disabled = true;
    announce(T('hold_still'));
    feat('night_start', { sec });
}
function holdStep(now) {
    const h = holdState;
    h.frames++;
    const f = Math.min(1, (now - h.t0) / 1000 / h.sec);
    el.ring.setAttribute('stroke-dashoffset', String(276.5 * (1 - f)));
    // a hand that moves a lot: say so while there is still time to steady it
    const sh = shakeRef();
    el.holdText.textContent = sh != null && sh > 4 ? T('hold_steady') : T('hold_left', { s: Math.max(0, Math.ceil(h.sec * (1 - f))) });
    if (f >= 1) {
        pendingShot = { kind: 'night', gesture: false, frames: h.frames };
        holdState = null;
        el.hold.hidden = true;
        el.shutter.dataset.state = 'idle'; el.shutter.disabled = false;
    }
}
function cancelHold() {
    if (!holdState) return;
    holdState = null;
    el.hold.hidden = true;
    el.shutter.dataset.state = 'idle'; el.shutter.disabled = !(source === 'camera');
}

/** Called right after a render, while the picture is on the canvas. */
async function takeShot(p) {
    let snap;
    try { snap = cleanSnapshot(); } catch (e) { oops(e, 'nv_snapshot', T('shot_failed')); return; }
    blink();
    if (p.gesture) { try { navigator.vibrate && navigator.vibrate(12); } catch (_) { } }
    try {
        const { blob, ext } = await encodePhoto(snap, S.format, 0.92);
        const d = new Date();
        const name = fileName(p.kind === 'night' ? 'fname_night' : p.kind === 'trap' ? 'fname_trap' : 'fname_photo', d, ext);
        const thumb = await thumbOf(snap);
        await addToRoll({ kind: p.kind, blob, thumb, w: snap.width, h: snap.height, name, ts: d.getTime(), mime: blob.type });
        if (p.gesture && S.autoSave) await window.__downloadFile(blob, name);
        if (p.kind === 'night') sess.night++; else sess.shots++;
        feat('shot', { kind: p.kind, w: snap.width, h: snap.height, look: S.look, stack: stackKey(), auto: S.stack === 'auto' ? 1 : 0, frames: p.frames || 0, fmt: ext, gain: last ? Math.round(last.gain) : 0, cam_mode: wsOn ? 1 : 0 });
        complete(p.kind, { w: snap.width, h: snap.height });
        if (p.kind !== 'trap') announce(T('saved'));
    } catch (e) { oops(e, 'nv_shot_encode', T('shot_failed')); }
}
function blink() {
    el.flash.classList.remove('on'); void el.flash.offsetWidth; el.flash.classList.add('on');
}
function fileName(key, d, ext) {
    const base = T(key) + '_' + d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0') + '_' + String(d.getHours()).padStart(2, '0') + String(d.getMinutes()).padStart(2, '0') + String(d.getSeconds()).padStart(2, '0');
    const safe = window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(base) : base;
    return safe + '.' + ext;
}
async function thumbOf(canvas) {
    const m = 320, k = Math.min(1, m / Math.max(canvas.width, canvas.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(canvas.width * k)); c.height = Math.max(1, Math.round(canvas.height * k));
    c.getContext('2d').drawImage(canvas, 0, 0, c.width, c.height);
    return window.__canvasBlob(c, 'image/jpeg', 0.8);
}

/* ── video: the canvas's own stream, so the recording is the picture — without the compare line ── */

let rec = null, recState = '', recBytes = 0, recT0 = 0, micStream = null, mic = null;
function recSec() { return recT0 ? (performance.now() - recT0) / 1000 : 0; }
async function startRecording() {
    if (!window.WebcamRecorder) { toast(T('rec_unsupported'), 'error'); return; }
    el.shutter.disabled = true;
    if (splitPos > 0) setSplit(false);
    el.compare.disabled = true;
    micStream = null;
    if (S.sound && window.MicManager) {
        try { mic = mic || new window.MicManager({ locale: window.__LOCALE, autoHelp: true }); micStream = await mic.start(); }
        catch (_) { micStream = null; toast(T('rec_no_audio'), 'info'); }
    }
    const canvasStream = el.canvas.captureStream(30);
    try {
        rec = new window.WebcamRecorder({
            quality: 'high', fps: 30, captureMic: !!micStream,
            acquire: () => Promise.resolve({ videoStream: canvasStream, micStream }),
            onProgress: (st) => { recBytes = (st && st.sizeBytesEstimate) || recBytes; },
            onError: (err) => { oops(err, 'nv_rec_runtime', T('rec_failed')); stopRecording(true); },
            onAudioMissing: () => 'continue',
            telemetry: (name, payload) => feat('rec_' + name, payload),
        });
        await rec.start();
    } catch (e) {
        rec = null;
        el.shutter.disabled = false; el.compare.disabled = false;
        oops(e, 'nv_rec_start', T('rec_failed'));
        try { canvasStream.getTracks().forEach((t) => t.stop()); } catch (_) { }
        return;
    }
    recState = 'on'; recBytes = 0; recT0 = performance.now();
    el.shutter.dataset.state = 'stop'; el.shutter.disabled = false;
    el.modes.querySelectorAll('.nv-mode').forEach((b) => { b.disabled = true; });
    try { window.__unsavedWork && window.__unsavedWork.arm('night-vision-recording'); } catch (_) { }
    feat('rec_start', { sound: micStream ? 1 : 0, w: procW, h: procH, cam_mode: wsOn ? 1 : 0 });
    announce(T('rec_started'));
}
async function stopRecording(silent) {
    if (!rec) { recState = ''; el.compare.disabled = false; return; }
    const r = rec; rec = null;
    recState = '';
    el.shutter.dataset.state = 'idle';
    el.compare.disabled = false;
    el.modes.querySelectorAll('.nv-mode').forEach((b) => { b.disabled = false; });
    let thumbCanvas = null;
    try { thumbCanvas = lane === 'gpu' && R.W ? R.snapshot() : cleanSnapshot(); } catch (_) { }
    const sec = recSec();
    let res = null;
    try { res = await r.stop(); } catch (e) { if (!silent) oops(e, 'nv_rec_stop', T('rec_failed')); }
    try { if (mic) mic.stop(); } catch (_) { }
    try { window.__unsavedWork && window.__unsavedWork.disarm(); } catch (_) { }
    recT0 = 0;
    if (!res || !res.blob || res.blob.size < 1000) return;
    sess.recs++; sess.recSec += sec;
    const d = new Date();
    const name = fileName('fname_video', d, res.ext || 'mp4');
    const thumb = thumbCanvas ? await thumbOf(thumbCanvas) : null;
    await addToRoll({ kind: 'video', blob: res.blob, thumb, w: procW, h: procH, name, ts: d.getTime(), mime: res.mime || res.blob.type, dur: (res.durationMs || 0) / 1000 });
    feat('rec_saved', { sec: Math.round((res.durationMs || 0) / 1000), mb: Math.round(res.blob.size / 1048576), pipe: res.pipeline || '', ext: res.ext || '' });
    complete('video', { sec: Math.round((res.durationMs || 0) / 1000) });
    if (!silent) toast(T('rec_saved'), 'success');
}

/* ───────────────────────── watch ───────────────────────── */

const watch = { on: false, w: null, last: 0 };
let alarmUntil = 0, audioCtx = null;
function setWatch(on) {
    watch.on = !!on;
    watch.w = on ? new MotionWatch({ sensitivity: S.watchSens / 100 }) : null;
    $('nvWatchBody').hidden = !on;
    $('nvStack').querySelector('[data-v="off"]').disabled = !!on;
    $('nvWatchSum').textContent = on ? T('watch_sum_on') : '';
    showWatchState();
    if (on) feat('watch_on', { sens: S.watchSens, trap: S.watchTrap ? 1 : 0 });
    else dimOff();
}
function watchStep(st) {
    if (!watch.w) return;
    watch.w.sensitivity = S.watchSens / 100;
    const r = watch.w.update(st, performance.now() / 1000);
    if (!r.event) return;
    watch.last = Date.now();
    alarmUntil = performance.now() + 3000;
    if (S.watchSound) beep();
    try { navigator.vibrate && navigator.vibrate([150, 80, 150]); } catch (_) { }
    if (S.watchTrap) pendingShot = { kind: 'trap', gesture: false };
    showWatchState();
    if (dimState) dimPeek();
    announce(T('watch_alarm'));
    feat('watch_event', { area: Math.round(r.area * 1000) / 10, n: watch.w.events, trap: S.watchTrap ? 1 : 0, dim: dimState ? 1 : 0 });
}
function showWatchState() {
    const n = watch.w ? watch.w.events : 0;
    $('nvWatchState').textContent = !watch.on ? '' : n ? T('watch_state_n', { n, time: new Date(watch.last).toTimeString().slice(0, 5) }) : T('watch_state_idle');
}
async function beep() {
    try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        const ctx = window.__liveAudioCtxReady ? await window.__liveAudioCtxReady(audioCtx) : audioCtx;
        audioCtx = ctx;
        const t = ctx.currentTime;
        for (let i = 0; i < 2; i++) {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
            g.gain.setValueAtTime(0.0001, t + i * 0.22);
            g.gain.exponentialRampToValueAtTime(0.3, t + i * 0.22 + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.22 + 0.16);
            o.start(t + i * 0.22); o.stop(t + i * 0.22 + 0.18);
        }
    } catch (_) { }
}

/*
 * The screen put out for a night watch: the camera keeps watching under a
 * black layer (an OLED screen is as good as off, the eyes and the room stay
 * dark). Motion shows the picture for 15 s, then the black comes back with the
 * time of the last event in dim red; a touch brings the picture back for good.
 */
let dimState = '', _dimT = 0;
function dimOn() {
    if (source !== 'camera' || !ws) return;
    if (!wsOn) enterCamMode('dim');
    closeSheet();
    dimState = 'on';
    el.dark.classList.remove('alarm');
    el.dark.textContent = T('dim_wake');
    el.dark.hidden = false;
    feat('dim', { watch: watch.on ? 1 : 0 });
}
function dimPeek() {
    dimState = 'peek';
    el.dark.hidden = true;
    clearTimeout(_dimT);
    _dimT = setTimeout(() => {
        if (dimState !== 'peek') return;
        dimState = 'on';
        el.dark.classList.add('alarm');
        el.dark.textContent = T('dim_last', { time: new Date(watch.last).toTimeString().slice(0, 5) });
        el.dark.hidden = false;
    }, 15000);
}
function dimOff() {
    if (!dimState) return;
    dimState = '';
    clearTimeout(_dimT);
    el.dark.hidden = true;
}

/* ───────────────────────── infrared test ───────────────────────── */

const ir = { on: false, probe: null, t0: 0, verdict: null };
async function startIr() {
    if (source !== 'camera') { await openCamera($('nvIrStart')); if (source !== 'camera') return; }
    closeSheet();
    ir.on = true; ir.probe = new IrProbe(); ir.t0 = performance.now(); ir.verdict = null;
    if (R) R.setGrid(160, 90);
    el.ir.hidden = false; el.irText.textContent = T('ir_step');
    feat('ir_start', { facing: cam.facing || '' });
}
function irStep(st) {
    const r = ir.probe.update(st, performance.now() / 1000);
    if (r.lit && r.spot) {
        el.irDot.hidden = false;
        el.irDot.style.left = (r.spot.x * 100).toFixed(1) + '%';
        el.irDot.style.top = (r.spot.y * 100).toFixed(1) + '%';
    } else el.irDot.hidden = true;
    if (r.seen && !ir.verdict) { ir.verdict = 'auto'; el.irText.textContent = T('ir_seen'); }
    else if (!ir.verdict && performance.now() - ir.t0 > 12000) el.irText.textContent = T('ir_waiting');
}
function endIr(answer) {
    if (!ir.on) return;
    const seen = answer === 'yes' || (answer == null && ir.verdict === 'auto');
    ir.on = false;
    el.ir.hidden = true; el.irDot.hidden = true;
    if (R) R.setGrid(64, 36);
    if (answer) {
        const label = (cam && cam.label) ? cam.label.replace(/\s*\(.*\)$/, '') : '';
        $('nvIrResult').textContent = seen ? T('ir_result_seen', { cam: label || T('this_camera') }) : T('ir_result_not', { cam: label || T('this_camera') });
        $('nvIrSum').textContent = seen ? T('ir_sum_yes') : T('ir_sum_no');
        feat('ir_result', { seen: seen ? 1 : 0, auto: ir.verdict === 'auto' ? 1 : 0, facing: (cam && cam.facing) || '', said: answer });
    }
}

/* ───────────────────────── files ───────────────────────── */

let file = null, fileUrl = '', still = null, exporting = false, exportAbort = null, limitRun = null;

async function openFile(f) {
    if (!f) return;
    await closeCamera();
    closeFile(true);
    ensureEngine();
    file = f;
    const isVideo = /^video\//.test(f.type) || /\.(mp4|mov|m4v|webm)$/i.test(f.name || '');
    stage('file_open');
    sess.files++;
    feat('file_open', { kind: isVideo ? 'video' : 'image', mb: Math.round(f.size / 1048576) });
    el.idle.hidden = true; el.fail.hidden = true;
    try {
        if (isVideo) await openVideoFile(f);
        else await openImageFile(f);
    } catch (e) {
        oops(e, 'nv_file_open', T('file_bad'));
        closeFile();
        return;
    }
    setControls();
    el.saveFrame.hidden = !isVideo;
    el.saveFile.textContent = isVideo ? T('save_video') : T('save_photo');
    el.playRow.hidden = !isVideo;
}

async function openImageFile(f) {
    const bmp = await createImageBitmap(f);
    // the photo's longest side on the GPU: the shared memory budget decides (iPhone's tab ceiling is fixed and low)
    const mb = window.__memBudget;
    const cap = mb && mb.constrained() ? 2400 : (mb && mb.gb() <= 4 ? 3000 : 4096);
    const k = Math.min(1, cap / Math.max(bmp.width, bmp.height));
    source = 'image';
    srcW = Math.round(bmp.width * k); srcH = Math.round(bmp.height * k);
    still = { bmp, noise: null };
    resizeEngine();
    // the noise of one picture, from neighbouring pixels
    try {
        const c = document.createElement('canvas'); c.width = procW; c.height = procH;
        const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(bmp, 0, 0, procW, procH);
        still.noise = C.fitNoiseSingle(x.getImageData(0, 0, procW, procH).data, procW, procH);
    } catch (_) { }
    ctl.reset(); last = null;
    renderStill(true);
}

/** A photo: one frame, rendered again on every change of a setting. */
function renderStill(prime) {
    if (source !== 'image' || !still) return;
    const run = () => {
        if (R) R.reset(); else cpu.P.reset();
        if (lane === 'gpu') {
            R.upload(still.bmp);
            const p = engineParams();
            p.stack = 1; p.noise = still.noise || p.noise;
            R.set(p);
            R.render(sourceMatrix({}));
            // a photo has no next frame for the asynchronous read-back: read it now
            const st = readStatsNow();
            if (st) last = ctl.update(st, 1, { ev: S.ev, manualGain: S.manGain ? Math.pow(2, S.gainLog) : 0 });
        } else {
            processFrame(still.bmp, 1);
        }
    };
    run();
    if (prime) { ctl.gainCtl.primed = false; run(); run(); }
    else run();
    updateHud();
}
function readStatsNow() {
    const gl = R.gl, g = R.grid;
    const buf = new Uint8Array(g.w * g.h * 2 * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, R.fboStats);
    gl.readPixels(0, 0, g.w, g.h * 2, gl.RGBA, gl.UNSIGNED_BYTE, buf);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const n = g.w * g.h * 4;
    return { raw: buf.subarray(0, n), avg: buf.subarray(n), w: g.w, h: g.h, frame: -1 };
}

async function openVideoFile(f) {
    const settled = window.FileBytes && window.FileBytes.settle ? await window.FileBytes.settle(f) : f;
    fileUrl = URL.createObjectURL(settled);
    const v = el.video;
    try { v.srcObject = null; } catch (_) { }
    v.muted = true; v.loop = false; v.playsInline = true;
    v.src = fileUrl;
    await new Promise((resolve, reject) => {
        const ok = () => { v.removeEventListener('loadeddata', ok); v.removeEventListener('error', bad); resolve(); };
        const bad = () => { v.removeEventListener('loadeddata', ok); v.removeEventListener('error', bad); reject(new Error('video-load')); };
        v.addEventListener('loadeddata', ok); v.addEventListener('error', bad);
    });
    source = 'video';
    srcW = v.videoWidth; srcH = v.videoHeight;
    resizeEngine();
    ctl.reset(); last = null;
    auto.reset();
    v.addEventListener('seeking', onSeeking);
    v.addEventListener('ended', onEnded);
    startLoop();
    try { await v.play(); } catch (_) { }
    paintPlay();
}
function onSeeking() { if (R) R.reset(); else if (cpu && cpu.P) cpu.P.reset(); }
function onEnded() { paintPlay(); }
function paintPlay() { const p = el.video.paused; el.play.textContent = p ? '▶' : '❚❚'; el.play.setAttribute('aria-label', p ? T('play') : T('pause')); }
function updateTime() {
    const v = el.video, d = isFinite(v.duration) ? v.duration : 0;
    el.time.textContent = fmtTime(v.currentTime) + ' / ' + fmtTime(d);
    if (d > 0 && document.activeElement !== el.seek) el.seek.value = String(Math.round(v.currentTime / d * 1000));
}

function closeFile(quiet) {
    if (exporting && exportAbort) exportAbort.abort();
    stopLoop();
    const v = el.video;
    try { v.pause(); } catch (_) { }
    v.removeEventListener('seeking', onSeeking); v.removeEventListener('ended', onEnded);
    if (fileUrl) { try { v.removeAttribute('src'); v.load(); } catch (_) { } URL.revokeObjectURL(fileUrl); fileUrl = ''; }
    if (still && still.bmp && still.bmp.close) still.bmp.close();
    still = null; file = null;
    if (source === 'image' || source === 'video') source = '';
    if (limitRun) { try { limitRun.abort(); } catch (_) { } limitRun = null; }
    if (!quiet) { setControls(); el.info.textContent = ''; }
}

/** «Сохранить» for a file: the photo, or the whole video through the pipeline. Billed per file. */
async function saveFromFile(kind) {
    if (!file || exporting) return;
    const key = window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null;
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, key) : null;
    if (window.__toolLimit && !run) return;   // the wall is already shown
    limitRun = run;
    try {
        let ok = false;
        if (kind === 'video') ok = await exportVideo();
        else ok = await saveStillFromFile(kind);
        if (ok) { if (run) run.commit(); }
        else if (run) run.abort();
    } catch (e) {
        if (run) run.abort();
        if (!(window.__isUserCancel && window.__isUserCancel(e, exportAbort && exportAbort.signal))) oops(e, 'nv_file_save', T('export_failed'));
    } finally { limitRun = null; }
}

async function saveStillFromFile(kind) {
    // the photo (or the current video frame) as it is on screen, at full processing size, without the compare line
    let snap;
    if (source === 'image') { renderStill(false); snap = cleanSnapshot(); }
    else {
        const v = el.video;
        if (!processFrame(v, 0.033)) { toast(T('shot_failed'), 'error'); return false; }
        snap = cleanSnapshot();
    }
    const { blob, ext } = await encodePhoto(snap, S.format, 0.92);
    const base = (file && file.name ? file.name.replace(/\.[^.]+$/, '') : 'photo') + '_' + T('fname_suffix');
    const name = (window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(base) : base) + '.' + ext;
    const ok = await window.__downloadFile(blob, name);
    if (ok === false) return false;
    const thumb = await thumbOf(snap);
    await addToRoll({ kind: 'file', blob, thumb, w: snap.width, h: snap.height, name, ts: Date.now(), mime: blob.type });
    feat('file_saved', { kind: kind === 'frame' ? 'frame' : 'photo', w: snap.width, h: snap.height, look: S.look });
    complete('file_photo', { w: snap.width, h: snap.height });
    return true;
}

async function exportVideo() {
    const W = window.WebCodecsPipeline;
    if (!W || !W.run || (W.canDemux && !W.canDemux(file))) { toast(T('export_unsupported'), 'error'); feat('export_unsupported', { type: file.type || '' }); return false; }
    exporting = true;
    el.video.pause(); paintPlay();
    stopLoop();
    exportAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
    el.exportProg.hidden = false; el.exportRow.hidden = false;
    el.saveFile.disabled = true; el.saveFrame.disabled = true; el.compare.disabled = true;
    const bar = el.exportProg.querySelector('i');
    bar.style.width = '0%'; el.exportText.textContent = T('exporting', { p: 0 });
    const gain0 = last ? last.gain : 1, noise0 = last && last.noise;
    if (R) R.reset(); else cpu.P.reset();
    // the export starts where the preview's exposure is: the controller then follows the clip
    ctl.gainCtl.gain = gain0; ctl.gainCtl.primed = true; if (noise0) ctl.noise = noise0;
    const t0 = performance.now();
    let lastProg = Date.now(), sawProg = false, stallReject;
    const stall = new Promise((_, rej) => { stallReject = rej; });
    const FIRST_MS = Math.max(90000, Math.round(file.size / 1048576 * 300)), GAP_MS = 30000;
    const watchdog = setInterval(() => {
        if (Date.now() - lastProg > (sawProg ? GAP_MS : FIRST_MS)) { clearInterval(watchdog); stallReject(new Error('nv-export-stalled')); }
    }, 5000);
    let frames = 0;
    // the file gets the picture, not the comparison
    const params = () => Object.assign(engineParams(), { split: 0 });
    try {
        const runP = W.run({
            slug: SLUG, file, params: {}, crf: 20,
            geometry2d: {
                width: srcW, height: srcH,
                draw: (ctx, drawVideo, OW, OH, i, tSec, frame, info) => {
                    if (!frame) throw new Error('nv-no-frame-arg');
                    if (OW !== procW || OH !== procH) {
                        procW = OW; procH = OH;
                        if (R) R.resize(OW, OH); else { el.canvas.width = OW; el.canvas.height = OH; cpu.tmp.width = OW; cpu.tmp.height = OH; cpu.P = new C.CpuPipeline(OW, OH); }
                        drawOverlay(true);
                    }
                    const rot = (info && info.rotation) || 0;
                    if (R) {
                        R.upload(info && info.sdr ? info.sdr() : frame);
                        R.set(params());
                        R.render(sourceMatrix({ rotate: rot }));
                        // exporting: wait for the read-back so the exposure follows the clip
                        const st = readStatsNow();
                        last = ctl.update(st, 1 / 30, { ev: S.ev, manualGain: S.manGain ? Math.pow(2, S.gainLog) : 0 });
                        ctx.drawImage(el.canvas, 0, 0, OW, OH);
                    } else {
                        // the CPU lane: the pipeline draws the frame upright, the night engine works on its pixels
                        drawVideo(0, 0, info.dispW, info.dispH, 0, 0, OW, OH);
                        const id = ctx.getImageData(0, 0, OW, OH);
                        const p = params();
                        const out = cpu.P.process(id.data, Object.assign({}, p, { lut: p.look === 'color' ? null : C.paletteLUT(p.look), zTest: true }));
                        ctx.putImageData(new ImageData(out, OW, OH), 0, 0);
                        if (overlayCanvas && overlayOn) ctx.drawImage(overlayCanvas, 0, 0, OW, OH);
                        last = ctl.update(cpu.P.stats(id.data, 48, 27, i), 1 / 30, { ev: S.ev, manualGain: S.manGain ? Math.pow(2, S.gainLog) : 0 });
                    }
                    frames++;
                },
            },
            abortSignal: exportAbort ? exportAbort.signal : undefined,
            onRestart: () => { lastProg = Date.now(); sawProg = false; },
            onProgress: (p) => {
                lastProg = Date.now(); sawProg = true;
                const pct = Math.max(0, Math.min(100, Math.round((p > 1 ? p / 100 : p) * 100)));
                bar.style.width = pct + '%';
                el.exportText.textContent = T('exporting', { p: pct });
            },
        });
        runP.catch(() => { });
        const r = await Promise.race([runP, stall]);
        if (!r || !r.blob || r.blob.size < 1000) throw new Error('nv-export-empty');
        const base = (file.name || 'video').replace(/\.[^.]+$/, '') + '_' + T('fname_suffix');
        const ext = /webm/.test(r.blob.type) ? 'webm' : 'mp4';
        const name = (window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(base) : base) + '.' + ext;
        const ok = await window.__downloadFile(r.blob, name);
        let thumb = null; try { thumb = await thumbOf(R ? R.snapshot() : el.canvas); } catch (_) { }
        await addToRoll({ kind: 'file', blob: r.blob, thumb, w: procW, h: procH, name, ts: Date.now(), mime: r.blob.type, dur: el.video.duration || 0 });
        feat('file_exported', { sec: Math.round((performance.now() - t0) / 1000), frames, mb: Math.round(r.blob.size / 1048576), look: S.look, lane });
        complete('file_video', { frames });
        toast(T('export_done'), 'success');
        return ok !== false;
    } finally {
        clearInterval(watchdog);
        exporting = false; exportAbort = null;
        el.exportProg.hidden = true; el.exportRow.hidden = true;
        el.saveFile.disabled = false; el.saveFrame.disabled = false; el.compare.disabled = false;
        if (source === 'video') { srcW = el.video.videoWidth; srcH = el.video.videoHeight; resizeEngine(); startLoop(); }
    }
}

/* ───────────────────────── the roll ───────────────────────── */

async function addToRoll(rec) {
    const id = await roll.add(rec);
    rollItems.push(Object.assign({ id }, rec));
    $('nvRollMem').hidden = roll.durable;
    paintRoll();
    if (rec.thumb) el.last.style.backgroundImage = 'url(' + thumbUrl(rollItems[rollItems.length - 1]) + ')';
    el.last.disabled = false;
}
const _urls = new Map();
function thumbUrl(r) {
    if (_urls.has(r.id)) return _urls.get(r.id);
    const u = URL.createObjectURL(r.thumb || r.blob);
    _urls.set(r.id, u);
    return u;
}
function paintRoll() {
    const g = $('nvRollGrid');
    g.textContent = '';
    $('nvRollEmpty').hidden = rollItems.length > 0;
    // how much the roll holds: it lives in this browser until it is cleared
    const mb = rollItems.reduce((s, r) => s + ((r.blob && r.blob.size) || 0), 0) / 1048576;
    $('nvRollCount').textContent = rollItems.length ? '(' + rollItems.length + ' · ' + (mb >= 10 ? Math.round(mb) : mb.toFixed(1)) + ' ' + T('unit_mb') + ')' : '';
    $('nvDlAll').disabled = !rollItems.length; $('nvDelAll').disabled = !rollItems.length;
    for (let i = rollItems.length - 1; i >= 0; i--) {
        const r = rollItems[i];
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'nv-cell';
        b.setAttribute('aria-label', r.name);
        const im = document.createElement('img'); im.alt = ''; im.loading = 'lazy';
        if (r.thumb || /^image\//.test(r.mime || '')) im.src = thumbUrl(r);
        const k = document.createElement('span'); k.className = 'nv-kind'; k.textContent = T('kind_' + r.kind) + (r.dur ? ' · ' + fmtTime(r.dur) : '');
        b.append(im, k);
        b.addEventListener('click', () => openViewer(i));
        g.appendChild(b);
    }
}
let viewerIdx = -1, viewerUrl = '';
function shareFile(r) {
    try {
        if (!navigator.canShare || !window.File) return null;
        const f = new File([r.blob], r.name, { type: r.mime || r.blob.type || '' });
        return navigator.canShare({ files: [f] }) ? f : null;
    } catch (_) { return null; }
}
function openViewer(i) {
    const r = rollItems[i]; if (!r) return;
    viewerIdx = i;
    const media = $('nvViewerMedia');
    media.textContent = '';
    if (viewerUrl) URL.revokeObjectURL(viewerUrl);
    viewerUrl = URL.createObjectURL(r.blob);
    if (/^video\//.test(r.mime || r.blob.type || '')) { const v = document.createElement('video'); v.controls = true; v.playsInline = true; v.src = viewerUrl; media.appendChild(v); }
    else { const im = document.createElement('img'); im.alt = r.name; im.src = viewerUrl; media.appendChild(im); }
    $('nvViewerName').textContent = r.name;
    $('nvViewerMeta').textContent = [r.w && r.h ? r.w + '×' + r.h : '', new Date(r.ts).toLocaleString(window.__intlLocale ? window.__intlLocale() : undefined), (r.blob.size / 1048576).toFixed(1) + ' ' + T('unit_mb')].filter(Boolean).join(' · ');
    $('nvVShare').hidden = !shareFile(r);
    $('nvVPrev').disabled = i <= 0; $('nvVNext').disabled = i >= rollItems.length - 1;
    const d = $('nvViewer');
    if (!d.open) { try { d.showModal(); } catch (_) { d.setAttribute('open', ''); } }
}
function closeViewer() { const d = $('nvViewer'); try { d.close(); } catch (_) { d.removeAttribute('open'); } $('nvViewerMedia').textContent = ''; if (viewerUrl) { URL.revokeObjectURL(viewerUrl); viewerUrl = ''; } }

/* ───────────────────────── camera mode and the settings sheet ───────────────────────── */

let ws = null, wsOn = false, fsToggle = null;
/** A phone: a coarse pointer and a screen whose short side is a phone's. */
function phoneLike() {
    try { return matchMedia('(pointer:coarse)').matches && Math.min(screen.width, screen.height) < 600; } catch (_) { return false; }
}
function enterCamMode(how) {
    if (!ws || wsOn) return;
    enterCamMode._how = how;
    ws.enter();
}
function openSheet() {
    if (!wsOn || !el.sheet.hidden) return;
    el.rail.querySelectorAll(':scope > details').forEach((d) => el.sheetBody.appendChild(d));
    el.sheet.hidden = false;
    el.settingsOpen.setAttribute('aria-expanded', 'true');
    sess.sheet++;
    feat('sheet', { open: 1 });
    try { el.sheetClose.focus({ preventScroll: true }); } catch (_) { }
}
function closeSheet() {
    if (el.sheet.hidden) return;
    Array.from(el.sheetBody.children).forEach((d) => el.rail.appendChild(d));
    el.sheet.hidden = true;
    el.settingsOpen.setAttribute('aria-expanded', 'false');
}

/* ───────────────────────── wiring ───────────────────────── */

function init() {
    // swatches
    el.looks.querySelectorAll('[data-swatch]').forEach((i) => { i.style.background = swatchCss(i.dataset.swatch); });
    el.looks.addEventListener('click', (e) => { const b = e.target.closest('.nv-look'); if (b) setLook(b.dataset.look, 'chip'); });
    setLook(S.look);
    el.modes.addEventListener('click', (e) => { const b = e.target.closest('.nv-mode'); if (b && !b.disabled) { setMode(b.dataset.mode); feat('mode', { m: b.dataset.mode }); } });
    setMode(S.mode);

    el.start.addEventListener('click', () => openCamera(el.start));
    el.retry.addEventListener('click', () => { try { cam && cam.mgr.stop(); } catch (_) { } openCamera(el.retry); });
    const pick = () => { const inp = el.drop && el.drop.querySelector('input[type=file]'); if (inp) inp.click(); };
    el.openFile.addEventListener('click', pick);
    el.failFile.addEventListener('click', pick);
    if (window.__bindUploaderFile && el.drop) window.__bindUploaderFile(el.drop, (f) => openFile(f));
    if (el.drop) el.drop.addEventListener('tool-uploader:clear', () => { if (source === 'image' || source === 'video') closeFile(); });

    el.shutter.addEventListener('click', shutter);
    el.flip.addEventListener('click', async () => {
        S.facing = (cam && cam.facing === 'user') ? 'environment' : 'user'; S.deviceId = ''; persist();
        feat('flip', { to: S.facing });
        await openCamera(el.flip, 'flip');
    });
    el.torch.addEventListener('click', toggleTorch);
    el.compare.addEventListener('click', () => setSplit(!(splitPos > 0)));
    el.last.addEventListener('click', () => { if (rollItems.length) openViewer(rollItems.length - 1); });
    el.tipClose.addEventListener('click', () => { if (tipKey) { tipClosed.add(tipKey); feat('tip_close', { k: tipKey }); } hideTip(); });

    // camera mode: the stage over the whole screen (real full screen, or an overlay on iPhone)
    if (typeof window.__wholeScreen === 'function') {
        ws = window.__wholeScreen(el.wrap, {
            key: 'night-vision',
            onChange: (on, mode) => {
                wsOn = on;
                if (!on) { closeSheet(); dimOff(); }
                redUi();
                const lbl = on ? T('exit_fullscreen') : T('fullscreen');
                el.fs.setAttribute('aria-label', lbl); el.fs.title = lbl;
                if (on) sess.camMode++;
                feat('cam_mode', { on: on ? 1 : 0, mode: mode || '', how: on ? (enterCamMode._how || 'button') : '' });
                enterCamMode._how = '';
            },
        });
        el.fs.addEventListener('click', () => { if (wsOn) ws.exit(); else enterCamMode('button'); });
        fsToggle = () => { if (wsOn) ws.exit(); else enterCamMode('key'); };
    } else { el.fs.hidden = true; $('nvDim').hidden = true; }
    el.settingsOpen.addEventListener('click', () => { if (el.sheet.hidden) openSheet(); else closeSheet(); });
    el.sheetClose.addEventListener('click', closeSheet);
    // a touch on the picture puts the sheet away
    el.vf.addEventListener('pointerdown', () => { if (!el.sheet.hidden) closeSheet(); });
    el.dark.addEventListener('click', () => { feat('dim_off', { state: dimState }); dimOff(); });
    $('nvDim').addEventListener('click', dimOn);
    bindCheck('nvCamMode', 'camMode');
    $('nvCamModeWrap').hidden = !phoneLike();

    // settings
    bindSeg('nvStack', 'stack', (v) => { if (v === 'auto') auto.reset(); paintStackHint(); if (source === 'image') renderStill(); });
    paintStackHint();
    bindSeg('nvDenoise', 'denoise', () => { if (source === 'image') renderStill(); });
    bindRange('nvEv', 'ev', (v) => { $('nvEvV').textContent = v === 0 ? T('ev_auto') : (v > 0 ? '+' : '') + v.toFixed(2).replace(/\.?0+$/, '') + ' EV'; }, () => { if (source === 'image') renderStill(); });
    bindCheck('nvManGain', 'manGain', (on) => { $('nvGainWrap').hidden = !on; if (source === 'image') renderStill(); });
    $('nvGainWrap').hidden = !S.manGain;
    bindRange('nvGain', 'gainLog', (v) => { $('nvGainV').textContent = '×' + fmtGain(Math.pow(2, v)); }, () => { if (source === 'image') renderStill(); });
    bindRange('nvShadows', 'shadows', (v) => { $('nvShadowsV').textContent = v + ' %'; }, () => { if (source === 'image') renderStill(); });
    bindRange('nvDetail', 'detail', (v) => { $('nvDetailV').textContent = v + ' %'; }, () => { if (source === 'image') renderStill(); });
    bindCheck('nvQuietColor', 'quietColor', () => { if (source === 'image') renderStill(); });
    $('nvPicReset').addEventListener('click', () => {
        for (const k of ['stack', 'denoise', 'ev', 'manGain', 'gainLog', 'shadows', 'detail', 'quietColor']) S[k] = DEFAULTS[k];
        persist();
        auto.reset();
        paintSeg($('nvStack'), S.stack); paintSeg($('nvDenoise'), S.denoise);
        $('nvEv').value = String(S.ev); $('nvEv').dispatchEvent(new Event('input'));
        $('nvManGain').checked = false; $('nvGainWrap').hidden = true;
        $('nvGain').value = String(S.gainLog); $('nvGain').dispatchEvent(new Event('input'));
        $('nvShadows').value = String(S.shadows); $('nvShadows').dispatchEvent(new Event('input'));
        $('nvDetail').value = String(S.detail); $('nvDetail').dispatchEvent(new Event('input'));
        $('nvQuietColor').checked = true;
        paintStackHint();
        if (source === 'image') renderStill();
        feat('reset');
    });

    $('nvDevice').addEventListener('change', async (e) => { S.deviceId = e.target.value; persist(); feat('device', { ir: isInfraredLabel(e.target.selectedOptions[0] && e.target.selectedOptions[0].textContent) ? 1 : 0 }); await openCamera($('nvDevice'), 'device'); });
    const resSel = $('nvRes'); resSel.value = S.res;
    resSel.addEventListener('change', async () => { S.res = resSel.value; persist(); feat('res', { v: S.res }); gov.scale = 1; if (source === 'camera') await openCamera(resSel, 'res'); else resizeEngine(); });
    $('nvZoom').addEventListener('input', (e) => setZoom(+e.target.value));
    bindSeg('nvSensor', 'sensor', (v) => { $('nvManual').hidden = v !== 'manual'; applySensor(); });
    bindRange('nvExp', 'expPos', () => showManualValues(), () => applySensor());
    bindRange('nvIso', 'isoPos', () => showManualValues(), () => applySensor());
    bindCheck('nvMirror', 'mirror', () => { if (R) R.reset(); else if (cpu && cpu.P) cpu.P.reset(); });

    bindSeg('nvTube', 'tube', () => { if (source === 'image') renderStill(); });
    bindRange('nvGrain', 'grain', (v) => { $('nvGrainV').textContent = v + ' %'; }, () => { if (source === 'image') renderStill(); });
    bindSeg('nvReticle', 'reticle', () => { drawOverlay(true); if (source === 'image') renderStill(); });
    bindCheck('nvStamp', 'stamp', () => { drawOverlay(true); if (source === 'image') renderStill(); });
    bindCheck('nvRedUi', 'redUi', () => redUi());

    bindCheck('nvWatchOn', 'watchOn', (on) => setWatch(on));
    bindRange('nvWatchSens', 'watchSens', (v) => { $('nvWatchSensV').textContent = v + ' %'; });
    bindCheck('nvWatchTint', 'watchTint');
    bindCheck('nvWatchSound', 'watchSound');
    bindCheck('nvWatchTrap', 'watchTrap');
    setWatch(S.watchOn);

    $('nvIrStart').addEventListener('click', startIr);
    $('nvIrYes').addEventListener('click', () => endIr('yes'));
    $('nvIrNo').addEventListener('click', () => endIr('no'));
    $('nvIrClose').addEventListener('click', () => endIr(null));

    bindSeg('nvFormat', 'format');
    bindSeg('nvNightLen', 'nightLen');
    bindCheck('nvSound', 'sound');
    bindCheck('nvAutoSave', 'autoSave');

    // file controls
    el.play.addEventListener('click', () => { const v = el.video; if (v.paused) { if (v.ended) v.currentTime = 0; v.play().catch(() => { }); } else v.pause(); setTimeout(paintPlay, 0); });
    el.video.addEventListener('play', paintPlay); el.video.addEventListener('pause', paintPlay);
    el.seek.addEventListener('input', () => { const v = el.video; if (isFinite(v.duration)) v.currentTime = (+el.seek.value / 1000) * v.duration; });
    el.saveFile.addEventListener('click', () => saveFromFile(source === 'video' ? 'video' : 'photo'));
    el.saveFrame.addEventListener('click', () => saveFromFile('frame'));
    el.closeFile.addEventListener('click', () => { closeFile(); feat('file_close'); });
    el.exportCancel.addEventListener('click', () => { if (exportAbort) exportAbort.abort(); feat('export_cancel'); });

    // roll
    roll.open().then(async () => {
        rollItems = await roll.list();
        $('nvRollMem').hidden = roll.durable;
        paintRoll();
        if (rollItems.length) { const l = rollItems[rollItems.length - 1]; el.last.disabled = false; el.last.style.backgroundImage = 'url(' + thumbUrl(l) + ')'; }
    });
    $('nvDlAll').addEventListener('click', async () => {
        if (!rollItems.length) return;
        if (rollItems.length === 1) { await window.__downloadFile(rollItems[0].blob, rollItems[0].name); return; }
        if (!window.__batchDeliver) { toast(T('batch_missing'), 'error'); return; }
        const d = new Date();
        try { await window.__batchDeliver.deliver(rollItems.map((x) => ({ name: x.name, blob: x.blob })), { zipName: T('fname_photo') + '_' + d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0') + '.zip', slug: SLUG }); feat('download_all', { n: rollItems.length }); }
        catch (e) { oops(e, 'nv_download_all', T('download_failed')); }
    });
    $('nvDelAll').addEventListener('click', async () => {
        if (!rollItems.length || !window.confirm(T('del_all_confirm'))) return;
        await roll.clear(); rollItems = []; for (const u of _urls.values()) URL.revokeObjectURL(u); _urls.clear();
        paintRoll(); el.last.disabled = true; el.last.style.backgroundImage = '';
        feat('roll_clear');
    });
    $('nvVClose').addEventListener('click', closeViewer);
    $('nvViewer').addEventListener('close', () => { $('nvViewerMedia').textContent = ''; });
    $('nvVPrev').addEventListener('click', () => { if (viewerIdx > 0) openViewer(viewerIdx - 1); });
    $('nvVNext').addEventListener('click', () => { if (viewerIdx < rollItems.length - 1) openViewer(viewerIdx + 1); });
    $('nvVDl').addEventListener('click', () => { const r = rollItems[viewerIdx]; if (r) { window.__downloadFile(r.blob, r.name); feat('download', { n: 1, kind: r.kind }); } });
    $('nvVShare').addEventListener('click', () => {
        const r = rollItems[viewerIdx]; const f = r && shareFile(r);
        if (!f) return;
        navigator.share({ files: [f] }).then(() => feat('share', { ok: 1, kind: r.kind }), (e) => { if (!(e && e.name === 'AbortError')) { feat('share', { ok: 0, kind: r.kind }); oops(e, 'nv_share', T('share_failed')); } });
    });
    $('nvVDel').addEventListener('click', async () => {
        const r = rollItems[viewerIdx]; if (!r) return;
        await roll.remove(r.id);
        if (_urls.has(r.id)) { URL.revokeObjectURL(_urls.get(r.id)); _urls.delete(r.id); }
        rollItems.splice(viewerIdx, 1);
        paintRoll(); closeViewer();
        if (!rollItems.length) { el.last.disabled = true; el.last.style.backgroundImage = ''; }
        else el.last.style.backgroundImage = 'url(' + thumbUrl(rollItems[rollItems.length - 1]) + ')';
    });

    // Escape: the sheet first, then what is open over the picture — before the overlay's own Escape closes camera mode
    window.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || $('nvViewer').open) return;
        if (!el.sheet.hidden) { closeSheet(); e.stopPropagation(); e.preventDefault(); return; }
        if (dimState) { dimOff(); e.stopPropagation(); e.preventDefault(); return; }
        if (ir.on) { endIr(null); e.stopPropagation(); return; }
        if (holdState) { cancelHold(); e.stopPropagation(); }
    }, true);
    // keys
    document.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
        if ($('nvViewer').open) return;
        const k = e.key;
        if ((k === ' ' || k === 'Enter') && source === 'camera') { e.preventDefault(); shutter(); return; }
        if ((k === 'r' || k === 'R' || k === 'к' || k === 'К') && source === 'camera') { e.preventDefault(); if (S.mode !== 'video' && !recState) setMode('video'); shutter(); return; }
        if ((k === 'f' || k === 'F' || k === 'а' || k === 'А') && fsToggle && source) { e.preventDefault(); fsToggle(); return; }
        if ((k === 'c' || k === 'C' || k === 'с' || k === 'С') && source && !el.compare.disabled) { e.preventDefault(); setSplit(!(splitPos > 0)); return; }
        if ((k === 't' || k === 'T' || k === 'е' || k === 'Е') && source === 'camera') { e.preventDefault(); toggleTorch(); return; }
        if ((k === '+' || k === '=') && source === 'camera') { e.preventDefault(); setZoom(zoomVal * 1.15); return; }
        if (k === '-' && source === 'camera') { e.preventDefault(); setZoom(zoomVal / 1.15); return; }
        const n = parseInt(k, 10);
        if (n >= 1 && n <= 8) { const b = el.looks.querySelectorAll('.nv-look')[n - 1]; if (b) { e.preventDefault(); setLook(b.dataset.look, 'key'); } }
    });

    // the page went away: give the camera back the way it was found
    window.addEventListener('pagehide', () => { sendSession(); if (source === 'camera') closeCamera(); });
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            hiddenAt = performance.now();
            sendSession();
            if (source === 'camera') { if (recState) stopRecording(true); cancelHold(); }
            return;
        }
        if (source === 'camera') resumeCamera();
    });

    window.__nv = {
        S, ctl, auto, get renderer() { return R; }, get lane() { return lane; }, get last() { return last; },
        get camMode() { return wsOn; }, get stack() { return stackKey(); }, openFile, roll: () => rollItems,
    };
    stage('ready');
}

init();
